import { useEffect, useRef, useState } from "react";
import { Icon } from "../components/Icon";
import { ProgressBar } from "../components/ProgressBar";
import { makeChoices, recordAnswer, scoreAnswer, shuffled, summarize } from "../lib/engine.mjs";
import { useLanguage, useStore } from "../app/useStore";
import { makeToken } from "../format";
import type { LanguageContext } from "../app/store";
import type { Word } from "../types";

type Mode = "deck" | "sprint";
type AnswerMode = "translation" | "target";

interface Result { id: string; selected: string | null; seconds: number; correct: boolean; timeout: boolean; replayed: boolean; points: number }
interface Round {
  active: boolean; phase: "listening" | "answer" | "feedback" | "done"; words: Word[]; index: number; results: Result[];
  answerMode: AnswerMode; type: Mode; focused: boolean; replayed: boolean; token: string; choices: Word[]; started: number;
}

const ROUND_SECONDS = 5;

/** Word browser (`deck`) and timed recognition rounds (`sprint`). Flashcards are FlashcardsPage. */
export function StudyPage({ itemId }: { itemId?: string }) {
  const ctx = useLanguage();
  const store = useStore();
  const { deck, progress: p } = ctx;

  const pool = deck;
  const label = "Whole deck";
  const reviewed = new Set<string>(p.tracks.study.reviewedWordIds);

  const [mode, setMode] = useState<Mode>(itemId === "deck" ? "deck" : "sprint");
  const [round, setRoundState] = useState<Round | null>(null);
  const roundRef = useRef<Round | null>(null);
  const setRound = (next: Round | null) => { roundRef.current = next; setRoundState(next); };

  const answerMode = (): AnswerMode => (p.prefs.english ? "translation" : "target");

  // ---- round lifecycle ----------------------------------------------------
  function nextQuestion(r: Round) {
    const w = r.words[r.index]!;
    const token = makeToken();
    setRound({ ...r, phase: "listening", replayed: false, token, choices: makeChoices(w, deck, r.answerMode) });
    ctx.playWord(w, true, { practiceToken: token });
  }

  function startRound() {
    ctx.player.pause();
    const words = shuffled(pool).slice(0, 20);
    ctx.activity("study", mode, label);
    nextQuestion({
      active: true, phase: "listening", words, index: 0, results: [], answerMode: answerMode(), type: mode,
      focused: false, replayed: false, token: "", choices: [], started: 0,
    });
  }

  function submit(selected: string | null, timeout = false) {
    const r = roundRef.current;
    if (!r || r.phase !== "answer") return;
    const w = r.words[r.index]!;
    const seconds = Math.min(ROUND_SECONDS, (performance.now() - r.started) / 1000);
    const correct = !timeout && seconds < ROUND_SECONDS && selected === w.id;
    const result: Result = {
      id: w.id, selected, seconds, correct, timeout: timeout || seconds >= ROUND_SECONDS, replayed: r.replayed,
      points: scoreAnswer(correct, seconds, r.replayed),
    };
    p.records[r.answerMode][w.id] = recordAnswer(p.records[r.answerMode][w.id], result);
    ctx.reviewWord(w);
    setRound({ ...r, results: [...r.results, result], phase: "feedback" });
    ctx.save();
  }

  function finishRound() {
    const r = roundRef.current;
    if (!r) return;
    const summary = summarize(r.results);
    p.sessions.push({ ...summary, at: Date.now(), answerMode: r.answerMode, type: r.type, count: r.results.length, focused: r.focused });
    p.sessions = p.sessions.slice(-100);
    setRound({ ...r, active: false, phase: "done" });
    ctx.save();
  }

  // The clock starts when the phrase ends.
  useEffect(() => store.onAudioComplete((program) => {
    const r = roundRef.current;
    if (r?.active && r.phase === "listening" && program.practiceToken === r.token) {
      setRound({ ...r, phase: "answer", started: performance.now() });
    }
  }), [store]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keyboard answers (1-4) and auto-timeout when the tab is hidden.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.matches?.("input,textarea,select,button,a") || document.querySelector("dialog[open]")) return;
      const r = roundRef.current;
      if (r?.phase === "answer" && /^[1-4]$/.test(e.key)) {
        e.preventDefault();
        const choice = r.choices[Number(e.key) - 1];
        if (choice) submit(choice.id);
      }
    };
    const onVisibility = () => { if (document.hidden && roundRef.current?.phase === "answer") submit(null, true); };
    document.addEventListener("keydown", onKey);
    document.addEventListener("visibilitychange", onVisibility);
    return () => { document.removeEventListener("keydown", onKey); document.removeEventListener("visibilitychange", onVisibility); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Leaving mid-round stops the practice clip.
  useEffect(() => () => {
    const r = roundRef.current;
    if (r?.active && ctx.player.getState().program?.practiceToken === r.token) ctx.player.stop();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const common = { ctx, label, mode, setMode, pool, reviewed, round, setRound, startRound, nextQuestion, finishRound, submit, answerMode } as const;
  if (mode === "deck") return <DeckView {...common} />;
  if (!round) return <Intro {...common} />;
  if (round.phase === "done") return <Summary {...common} round={round} />;
  return <Question {...common} round={round} />;
}

type Shared = {
  ctx: LanguageContext; label: string; mode: Mode; setMode(m: Mode): void; pool: Word[]; reviewed: Set<string>;
  round: Round | null; setRound(r: Round | null): void; startRound(): void; nextQuestion(r: Round): void; finishRound(): void;
  submit(selected: string | null, timeout?: boolean): void; answerMode(): AnswerMode;
};

function Top({ ctx, title, copy }: { ctx: LanguageContext; title: string; copy: string }) {
  return (
    <>
      <a className="back-link" href={ctx.href("study")}><Icon name="back" /> Study</a>
      <div className="view-heading"><div><p className="overline">{ctx.definition.name} study</p><h1>{title}</h1><p>{copy}</p></div></div>
    </>
  );
}

function Settings({ ctx, locked }: { ctx: LanguageContext; locked: boolean }) {
  const { progress: p, definition: d } = ctx;
  return (
    <div className="settings-row">
      <label className="toggle-label"><input id="english-toggle" type="checkbox" checked={p.prefs.english} disabled={locked}
        onChange={(e) => { p.prefs.english = e.target.checked; ctx.save(); }} /> English meanings</label>
      {d.capabilities.reading && (
        <label className="toggle-label"><input id="reading-toggle" type="checkbox" checked={p.prefs.reading} disabled={locked}
          onChange={(e) => { p.prefs.reading = e.target.checked; ctx.save(); }} /> Pronunciation hints</label>
      )}
      <span className="hint">{p.prefs.english ? "Translation answers" : d.name + " answers"}</span>
    </div>
  );
}

function DeckView({ ctx, reviewed }: Shared) {
  const { deck, definition: d } = ctx;
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const rows = deck.filter((w) => [w.word, w.reading, w.meaning, w.targetDefinition].some((x) => (x || "").toLowerCase().includes(q)));
  return (
    <>
      <Top ctx={ctx} title="Your word deck." copy={`${deck.length} entries to explore at your own pace.`} />
      <input className="search-input" id="deck-search" type="search" aria-label="Find a word" placeholder="Find a word or meaning…" value={query} onChange={(e) => setQuery(e.target.value)} />
      <div id="deck-rows" className="word-list">
        {rows.map((w) => (
          <button key={w.id} className="word-row" onClick={() => ctx.openWord(w.id)}>
            <span className="entry-no">{w.position}</span>
            <strong lang={d.id}>{w.word}</strong>
            <span className="reading" lang={d.id}>{d.capabilities.reading ? w.reading : ""}</span>
            <span>{w.meaning}</span>
            <Icon name={reviewed.has(w.id) ? "check" : "next"} />
          </button>
        ))}
        {!rows.length && <p className="empty-state">No matching words.</p>}
      </div>
    </>
  );
}

function Intro(s: Shared) {
  const { ctx, pool, startRound } = s;
  const count = Math.min(20, pool.length);
  return (
    <>
      <Top ctx={ctx} title="Find your listening rhythm." copy="Hear a phrase, then choose the meaning. Accuracy comes first." />
      <section className="round-intro">
        <div className="large-icon"><Icon name="audio" /></div>
        <h2>Ready when you are.</h2>
        <p>{count} questions. Five seconds to answer after each phrase. Replay any phrase for accuracy points without the speed bonus.</p>
        <Settings ctx={ctx} locked={false} />
        <button className="button" id="start-round" onClick={startRound}>Start speed round</button>
      </section>
    </>
  );
}

function Question({ round, ctx, setRound, nextQuestion, finishRound, submit }: Shared & { round: Round }) {
  const store = useStore();
  const { definition: d } = ctx;
  const answered = round.phase === "feedback";
  const r = round.results.at(-1);
  const w = round.words[round.index]!;
  const target = round.answerMode === "target";
  const [left, setLeft] = useState(ROUND_SECONDS);

  // Countdown while the answer window is open; times out at zero.
  useEffect(() => {
    if (round.phase !== "answer") { setLeft(ROUND_SECONDS); return; }
    const id = setInterval(() => {
      const remaining = Math.max(0, ROUND_SECONDS - (performance.now() - round.started) / 1000);
      setLeft(remaining);
      if (remaining === 0) submit(null, true);
    }, 50);
    return () => clearInterval(id);
  }, [round.phase, round.started]); // eslint-disable-line react-hooks/exhaustive-deps

  const ps = store.playerState;
  const playbackError = !!ps.error && ps.program?.practiceToken === round.token;
  const answerText = (x: Word) => (target ? x.targetDefinition || x.ja : x.meaning);

  const replay = () => {
    if (answered) { ctx.playWord(w, true); return; }
    const token = makeToken();
    setRound({ ...round, replayed: true, phase: "listening", token });
    ctx.playWord(w, true, { practiceToken: token });
  };

  return (
    <>
      <Top ctx={ctx} title="Speed round" copy="Listen first. The clock starts when the phrase ends." />
      <section className="word-card quiz-card">
        <div className="card-top"><span>Question {round.index + 1} of {round.words.length}</span><span>{Math.round(round.results.reduce((sum, x) => sum + x.points, 0))} points</span></div>
        <ProgressBar done={round.index} total={round.words.length} label="Round progress" />
        <div className="word-face">
          <button className="sound-button" id="play-word" aria-label="Replay phrase" onClick={replay}><Icon name="sound" /></button>
          <h2 className="target-word" lang={d.id}>{w.word}</h2>
          {d.capabilities.reading && ctx.progress.prefs.reading && <p className="reading" lang={d.id}>{w.reading}</p>}
          <p className="meaning">{answered ? (ctx.progress.prefs.english ? w.meaning : w.targetDefinition || w.ja) : "Listen, then choose the meaning"}</p>
          <div className="phrase">
            <p lang={d.id}>{w.phrase}</p>
            {answered && ctx.progress.prefs.english && <p className="phrase-translation">{w.translation}</p>}
          </div>
          <p className="error-message" id="practice-error" role="alert">{playbackError ? "Audio couldn’t play. Tap the speaker to retry." : ""}</p>
        </div>
        <div id="answer-area">
          {round.phase === "answer" || answered ? (
            <>
              <div className="answer-heading">
                <span>{answered ? "Meaning in this phrase" : "What does this word mean?"}</span>
                <strong id="timer-value">{answered && r ? r.seconds.toFixed(1) : left.toFixed(1)}s</strong>
              </div>
              <div className="answer-grid">
                {round.choices.map((choice, i) => (
                  <button key={choice.id} disabled={answered} onClick={() => submit(choice.id)}
                    className={"answer-option " + (answered ? (choice.id === w.id ? "correct" : choice.id === r?.selected ? "incorrect" : "") : "")}>
                    <kbd>{i + 1}</kbd><span lang={target ? d.id : undefined}>{answerText(choice)}</span>
                  </button>
                ))}
              </div>
              {answered && r && (
                <div className="round-feedback">
                  <div>
                    <strong>{r.correct ? "Correct · +" + Math.round(r.points) + " points" : r.timeout ? "Time’s up. Another encounter will help." : "One to listen to again."}</strong>
                    <p>{r.replayed ? "Replay used · accuracy points only" : r.correct ? "Keep finding your rhythm." : answerText(w)}</p>
                  </div>
                  <button className="button" id="next-question" ref={(el) => el?.focus({ preventScroll: true })}
                    onClick={() => (round.index === round.words.length - 1 ? finishRound() : nextQuestion({ ...round, index: round.index + 1 }))}>
                    {round.index === round.words.length - 1 ? "See results" : "Next word"} <Icon name="next" />
                  </button>
                </div>
              )}
            </>
          ) : <p className="listening-status" role="status">Listening… Your choices appear when the phrase ends.</p>}
        </div>
      </section>
    </>
  );
}

function Summary({ round, ctx, setRound }: Shared & { round: Round }) {
  const s = summarize(round.results);
  const { deck, definition: d } = ctx;
  const target = round.answerMode === "target";
  return (
    <>
      <Top ctx={ctx} title="Every encounter adds up." copy="Your round is saved to this language’s stats." />
      <section className="round-summary">
        <span className="score-number">{Math.round(s.score)}<small>/100</small></span>
        <h2>{s.accuracy >= 80 ? "That’s starting to click." : "Keep giving your ear time."}</h2>
        <div className="summary-metrics">
          <div><strong>{Math.round(s.accuracy)}%</strong><span>Accuracy</span></div>
          <div><strong>{s.time === null ? "—" : s.time.toFixed(1) + "s"}</strong><span>Median correct time</span></div>
          <div><strong>{s.correct}/{round.results.length}</strong><span>Understood</span></div>
        </div>
        <div className="button-row">
          <button className="button" id="another-round" onClick={() => setRound(null)}>Practice again</button>
          <a className="button secondary" href={ctx.href("stats")}>See your stats</a>
        </div>
      </section>
      <div className="result-list">
        {round.results.map((r) => {
          const w = deck.find((x) => x.id === r.id)!;
          return (
            <div key={r.id}>
              <strong lang={d.id}>{w.word}</strong>
              <span>{target ? w.targetDefinition || w.ja : w.meaning}</span>
              <span className={r.correct ? "positive" : "negative"}>{r.correct ? r.seconds.toFixed(1) + "s" : r.timeout ? "Timed out" : "Review"}</span>
            </div>
          );
        })}
      </div>
    </>
  );
}
